/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  HttpClient as AngularHttpClient,
  HttpResponse as AngularHttpResponse,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { IStorageRepository } from '../../../domain/storage';
import { HttpResponse, IHttpClient, UNAUTHORIZED_CODE, UNDER_MAINTENANCE_CODE } from './interface';
import { rightValue } from '../../../util/either';

const AD_AUTH_PREFIX = '/ad-auth';

@Injectable({
  providedIn: 'root',
})
export class HttpClient implements IHttpClient {
  private angularHttpClient = inject(AngularHttpClient);
  private storageRepository = inject(IStorageRepository);

  public async get<T = any>(urlWithQueryParams: string): Promise<HttpResponse<T>> {
    const response = await lastValueFrom(
      this.angularHttpClient.get<T>(this.buildUrl(urlWithQueryParams), {
        observe: 'response',
        headers: this.getHeaders(),
      })
    );
    return this.processResponse<T>(response);
  }

  public async post<T = any>(url: string, body: any): Promise<HttpResponse<T>> {
    const response = await lastValueFrom(
      this.angularHttpClient.post<T>(this.buildUrl(url), body, {
        observe: 'response',
        headers: this.getHeaders(),
      })
    );
    return this.processResponse<T>(response);
  }

  public async postFormData<T = any>(url: string, formData: FormData): Promise<HttpResponse<T>> {
    const response = await lastValueFrom(
      this.angularHttpClient.post<T>(this.buildUrl(url), formData, {
        observe: 'response',
        headers: this.getHeaders(false),
      })
    );
    return this.processResponse<T>(response);
  }

  public async put<T = any>(url: string, body: any): Promise<HttpResponse<T>> {
    const response = await lastValueFrom(
      this.angularHttpClient.put<T>(this.buildUrl(url), body, {
        observe: 'response',
        headers: this.getHeaders(),
      })
    );
    return this.processResponse<T>(response);
  }

  public async patch<T = any>(url: string, body: any): Promise<HttpResponse<T>> {
    const response = await lastValueFrom(
      this.angularHttpClient.patch<T>(this.buildUrl(url), body, {
        observe: 'response',
        headers: this.getHeaders(),
      })
    );
    return this.processResponse<T>(response);
  }

  public async delete<T = any>(url: string): Promise<HttpResponse<T>> {
    const response = await lastValueFrom(
      this.angularHttpClient.delete<T>(this.buildUrl(url), {
        observe: 'response',
        headers: this.getHeaders(),
      })
    );
    return this.processResponse<T>(response);
  }

  public async getBlob(url: string): Promise<HttpResponse<Blob>> {
    const response = await lastValueFrom(
      this.angularHttpClient.get(this.buildUrl(url), {
        observe: 'response',
        responseType: 'blob',
        headers: this.getHeaders(false),
      })
    );
    return this.processResponse<Blob>(response as AngularHttpResponse<Blob>);
  }

  public getToken(): string {
    const stored = this.storageRepository.getStorage('token');
    return stored.isRight() ? rightValue(stored) : '';
  }

  public buildUrl(url: string): string {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    const path = url.startsWith('/') ? url : `/${url}`;

    if (path.startsWith(AD_AUTH_PREFIX)) {
      return `${environment.adAuthBaseUrl}${path}`;
    }

    return `${environment.apiBaseUrl}${path}`;
  }

  private getHeaders(withJsonContentType = true): Record<string, string> {
    const headers: Record<string, string> = {
      accept: 'application/json, text/plain, */*',
    };

    if (withJsonContentType) {
      headers['content-type'] = 'application/json';
    }

    const token = this.getToken();

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  private processResponse<T = any>(response: AngularHttpResponse<T>): HttpResponse<T> {
    if (response.status >= 200 && response.status <= 299) {
      return {
        body: response.body as T,
        status: response.status,
        url: response.url ?? '',
      };
    }

    if (response.status === UNDER_MAINTENANCE_CODE || response.status === UNAUTHORIZED_CODE) {
      throw response;
    }

    throw response;
  }
}
